import React, {useEffect, useMemo, useRef, useState} from 'react';
import gql from 'graphql-tag';
import {Alert, Badge, Button, Form, Modal, Table} from 'react-bootstrap';
import {
    FeedPostMediaAssetKind,
    MediaOptimizationStatus,
    useAdminFeedPostMediaAssetsQuery,
    useOptimizeFeedPostMediaMutation,
    useRetryFeedPostMediaOptimizationMutation,
} from 'types/gqlReactTypings.generated.d';
import {formatDate} from 'shared/Utilities';

interface FeedMediaOptimizationModalProps {
    feedPostId: number | null;
    show: boolean;
    onHide: () => void;
}

const POLL_INTERVAL_MS = 3000;

const ACTIVE_STATUSES = [MediaOptimizationStatus.Queued, MediaOptimizationStatus.Processing];

const statusVariant = (status: MediaOptimizationStatus): string => {
    switch (status) {
        case MediaOptimizationStatus.Ready:
            return 'success';
        case MediaOptimizationStatus.Failed:
            return 'danger';
        case MediaOptimizationStatus.Unsupported:
            return 'secondary';
        case MediaOptimizationStatus.Queued:
        case MediaOptimizationStatus.Processing:
            return 'warning';
        case MediaOptimizationStatus.Stale:
            return 'dark';
        default:
            return 'light';
    }
};

const formatBytes = (bytes?: string | null): string => {
    if (!bytes) return '-';
    const value = Number(bytes);
    if (!Number.isFinite(value) || value <= 0) return '-';
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = value;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024;
        unitIndex += 1;
    }
    return `${size.toFixed(1)} ${units[unitIndex]}`;
};

const savingsPercent = (sourceBytes?: string | null, optimizedBytes?: string | null): string => {
    const source = sourceBytes ? Number(sourceBytes) : 0;
    const optimized = optimizedBytes ? Number(optimizedBytes) : 0;
    if (!source || !optimized) return '-';
    const percent = ((source - optimized) / source) * 100;
    return percent >= 0 ? `${percent.toFixed(0)}%` : `${Math.abs(percent).toFixed(0)}% larger`;
};

const kindLabel = (kind: FeedPostMediaAssetKind, position: number): string => {
    switch (kind) {
        case FeedPostMediaAssetKind.Video:
            return 'Video';
        case FeedPostMediaAssetKind.VideoThumbnail:
            return 'Thumbnail';
        case FeedPostMediaAssetKind.CarouselImage:
            return `Image ${position + 1}`;
        default:
            return kind;
    }
};

export const FeedMediaOptimizationModal: React.FC<FeedMediaOptimizationModalProps> = ({feedPostId, show, onHide}) => {
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [previewVariant, setPreviewVariant] = useState<Record<string, 'original' | 'optimized'>>({});
    const [error, setError] = useState<string | undefined>();
    const [busy, setBusy] = useState(false);
    const pollTimer = useRef<ReturnType<typeof setInterval>>();

    const query = useAdminFeedPostMediaAssetsQuery({
        variables: {feedPostId: feedPostId ?? 0},
        skip: !show || feedPostId == null,
        fetchPolicy: 'network-only',
    });
    const [optimizeMutation] = useOptimizeFeedPostMediaMutation();
    const [retryMutation] = useRetryFeedPostMediaOptimizationMutation();

    const assets = query.data?.adminFeedPostMediaAssets ?? [];
    const hasActiveJob = assets.some(asset => ACTIVE_STATUSES.includes(asset.status) || !!asset.activeJob);

    useEffect(() => {
        if (show && feedPostId != null && hasActiveJob) {
            pollTimer.current = setInterval(() => {
                query.refetch();
            }, POLL_INTERVAL_MS);
        }
        return () => {
            if (pollTimer.current) {
                clearInterval(pollTimer.current);
                pollTimer.current = undefined;
            }
        };
    }, [show, feedPostId, hasActiveJob]);

    useEffect(() => {
        if (!show) {
            setSelectedIds(new Set());
            setPreviewVariant({});
            setError(undefined);
        }
    }, [show]);

    const failedAssetIds = useMemo(
        () => assets.filter(asset => asset.status === MediaOptimizationStatus.Failed).map(asset => asset.id),
        [assets],
    );

    const toggleSelected = (id: string) => {
        setSelectedIds(current => {
            const next = new Set(current);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const runMutation = async (action: () => Promise<any>) => {
        setBusy(true);
        setError(undefined);
        try {
            await action();
            await query.refetch();
        } catch (err: any) {
            setError(err?.message ?? 'Media optimization request failed.');
        } finally {
            setBusy(false);
        }
    };

    const handleOptimizeSelected = () => {
        if (feedPostId == null || selectedIds.size === 0) return;
        runMutation(() => optimizeMutation({
            variables: {input: {feedPostId, assetIds: Array.from(selectedIds), force: false}},
        }));
    };

    const handleReoptimizeSelected = () => {
        if (feedPostId == null || selectedIds.size === 0) return;
        if (!window.confirm('Re-optimize the selected assets? The original file will not be changed or removed.')) {
            return;
        }
        runMutation(() => optimizeMutation({
            variables: {input: {feedPostId, assetIds: Array.from(selectedIds), force: true}},
        }));
    };

    const handleRetryFailed = () => {
        if (failedAssetIds.length === 0) return;
        runMutation(() => Promise.all(failedAssetIds.map(assetId => retryMutation({variables: {assetId}}))));
    };

    const selectedReadyCount = assets.filter(asset => selectedIds.has(asset.id) && asset.status === MediaOptimizationStatus.Ready).length;

    return (
        <Modal show={show} onHide={onHide} size='xl'>
            <Modal.Header closeButton>
                <Modal.Title>Media optimization — Feed post #{feedPostId}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Alert variant='info'>
                    Optimization creates a separate, smaller copy of each file. The original file is never changed
                    or deleted, and stays available at any time.
                </Alert>
                {error ? <Alert variant='danger'>{error}</Alert> : null}
                {query.error ? <Alert variant='danger'>Could not load media assets.</Alert> : null}
                <Table responsive size='sm'>
                    <thead>
                    <tr>
                        <th></th>
                        <th>Preview</th>
                        <th>Kind</th>
                        <th>Status</th>
                        <th>Original</th>
                        <th>Optimized</th>
                        <th>Savings</th>
                        <th>Optimized at</th>
                        <th>Links</th>
                    </tr>
                    </thead>
                    <tbody>
                    {assets.map(asset => {
                        const variant = previewVariant[asset.id] ?? 'original';
                        const previewUrl = variant === 'optimized' && asset.optimizedUrl ? asset.optimizedUrl : asset.sourceUrl;
                        const isVideo = asset.kind === FeedPostMediaAssetKind.Video;
                        return (
                            <tr key={asset.id}>
                                <td>
                                    <Form.Check
                                        type='checkbox'
                                        checked={selectedIds.has(asset.id)}
                                        onChange={() => toggleSelected(asset.id)}
                                    />
                                </td>
                                <td style={{width: 140}}>
                                    {isVideo ? (
                                        <video src={previewUrl} style={{height: 90, width: 120}} controls/>
                                    ) : (
                                        <img src={previewUrl} alt={kindLabel(asset.kind, asset.position)}
                                             style={{height: 90, objectFit: 'cover', width: 120}}/>
                                    )}
                                    <Form.Check
                                        type='switch'
                                        id={`preview-toggle-${asset.id}`}
                                        label={variant === 'optimized' ? 'Optimized' : 'Original'}
                                        checked={variant === 'optimized'}
                                        disabled={!asset.optimizedUrl}
                                        onChange={event => setPreviewVariant(current => ({
                                            ...current,
                                            [asset.id]: event.target.checked ? 'optimized' : 'original',
                                        }))}
                                    />
                                </td>
                                <td>{kindLabel(asset.kind, asset.position)}</td>
                                <td>
                                    <Badge variant={statusVariant(asset.status)}>{asset.status}</Badge>
                                    {asset.activeJob ? (
                                        <div className='text-muted' style={{fontSize: 11}}>
                                            attempt {asset.activeJob.attempt}/{asset.activeJob.maxAttempts}
                                        </div>
                                    ) : null}
                                    {(asset.status === MediaOptimizationStatus.Failed || asset.status === MediaOptimizationStatus.Unsupported) && asset.lastErrorMessage ? (
                                        <div className='text-danger' style={{fontSize: 11, maxWidth: 160}}>
                                            {asset.lastErrorCode}: {asset.lastErrorMessage}
                                        </div>
                                    ) : null}
                                </td>
                                <td>
                                    {asset.sourceContentType ?? '-'}<br/>
                                    {formatBytes(asset.sourceSizeBytes)}
                                </td>
                                <td>
                                    {asset.optimizedContentType ?? '-'}<br/>
                                    {asset.width && asset.height ? `${asset.width}x${asset.height}` : ''}<br/>
                                    {asset.optimizedUrl ? formatBytes(asset.optimizedSizeBytes) : ''}
                                </td>
                                <td>{savingsPercent(asset.sourceSizeBytes, asset.optimizedSizeBytes)}</td>
                                <td>{asset.optimizedAt ? formatDate(asset.optimizedAt) : '-'}</td>
                                <td>
                                    <a href={asset.sourceUrl} target='_blank' rel='noopener noreferrer'>Open original</a>
                                    <br/>
                                    {asset.optimizedUrl ? (
                                        <a href={asset.optimizedUrl} target='_blank' rel='noopener noreferrer'>Open optimized</a>
                                    ) : (
                                        <span className='text-muted'>Open optimized</span>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                    {assets.length === 0 && !query.loading ? (
                        <tr>
                            <td colSpan={9} className='text-muted'>No media assets found for this post.</td>
                        </tr>
                    ) : null}
                    </tbody>
                </Table>
            </Modal.Body>
            <Modal.Footer>
                <Button variant='secondary' onClick={onHide} disabled={busy}>Close</Button>
                {failedAssetIds.length > 0 ? (
                    <Button variant='outline-warning' disabled={busy} onClick={handleRetryFailed}>
                        Retry failed ({failedAssetIds.length})
                    </Button>
                ) : null}
                <Button
                    variant='primary'
                    disabled={busy || selectedIds.size === 0}
                    onClick={handleOptimizeSelected}
                >
                    Optimize selected ({selectedIds.size})
                </Button>
                {selectedReadyCount > 0 ? (
                    <Button variant='outline-primary' disabled={busy} onClick={handleReoptimizeSelected}>
                        Re-optimize selected ({selectedReadyCount})
                    </Button>
                ) : null}
            </Modal.Footer>
        </Modal>
    );
};

gql`
    query AdminFeedPostMediaAssets($feedPostId: Int!) {
        adminFeedPostMediaAssets(feedPostId: $feedPostId) {
            id
            kind
            position
            sourceUrl
            optimizedUrl
            displayUrl
            status
            profileKey
            sourceContentType
            optimizedContentType
            sourceSizeBytes
            optimizedSizeBytes
            width
            height
            durationMs
            lastErrorCode
            lastErrorMessage
            optimizedAt
            activeJob {
                id
                status
                trigger
                attempt
                maxAttempts
                errorCode
                errorMessage
                createdAt
                startedAt
                finishedAt
            }
        }
    }

    mutation OptimizeFeedPostMedia($input: OptimizeFeedPostMediaInput!) {
        optimizeFeedPostMedia(input: $input) {
            acceptedCount
            skippedCount
            assets {
                id
                status
            }
            jobs {
                id
                status
            }
        }
    }

    mutation RetryFeedPostMediaOptimization($assetId: ID!) {
        retryFeedPostMediaOptimization(assetId: $assetId) {
            acceptedCount
            skippedCount
            assets {
                id
                status
            }
            jobs {
                id
                status
            }
        }
    }
`;
